/**
 * طبقة الاتصال لشاشة «جروبات الأسرة» (docs/03 §11).
 *
 * الراوتر: `app/routers/groups.py`.
 *
 * ⛔ القواعد المحروسة هنا:
 *   1) **جروب الإشعارات الافتراضي واحد بس لكل أسرة** — الشاشة بتعرض
 *      التحذير اللي جاي من السيرفر (`DEFAULT_GROUP_UNSET`) زي ما هو،
 *      وما بتخترعش نص من عندها.
 *   2) **حزمة التهيئة سبع بنود مقفولة** — بتيجي من السيرفر بأسمائها
 *      العربية، والواجهة ما بتعرّفهاش تاني عشان ما يبقاش فيه مصدرين.
 *   3) ⛔ مفيش حذف — الأرشفة بسبب إجباري.
 */
import { api } from "@/api/client";

export type ChecklistItem = { key: string; labelAr: string; done: boolean };

export type GroupRow = {
  id: string;
  type: string;
  typeLabelAr: string;
  nameAr: string;
  familyId: string | null;
  student: { id: string | null; nameAr: string } | null;
  enrollment: { id: string | null; labelAr: string } | null;
  platform: string;
  platformLabelAr: string;
  externalRef: string | null;
  status: string;
  statusLabelAr: string;
  isNotificationDefault: boolean;
  membersCount: number;
  checklist: ChecklistItem[];
  checklistDone: number;
  checklistTotal: number;
  checklistComplete: boolean;
  version: number;
};

export type PendingGroupWork = {
  journeyId: string;
  stepNo: number;
  enrollment: { id: string; labelAr: string; subject: string | null };
  family: { id: string; nameAr: string };
};

export type GroupWarning = { code: string; messageAr: string };

function fail(path: string, why: string): never {
  console.error(`[groups] ${path} — ${why}`);
  throw new Error(`${path}: ${why}`);
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}
function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function num(v: unknown, dflt = 0): number {
  return typeof v === "number" ? v : dflt;
}
function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

function toGroup(d: Record<string, unknown>): GroupRow {
  const student = d["student"] ? obj(d["student"]) : null;
  const enrollment = d["enrollment"] ? obj(d["enrollment"]) : null;
  const list = Array.isArray(d["checklist"]) ? (d["checklist"] as Record<string, unknown>[]) : [];
  return {
    id: str(d["id"]),
    type: str(d["type"]),
    typeLabelAr: str(d["type_label_ar"]),
    nameAr: str(d["name_ar"]),
    familyId: strOrNull(d["family_id"]),
    student: student
      ? { id: strOrNull(student["id"]), nameAr: str(student["name_ar"]) }
      : null,
    enrollment: enrollment
      ? { id: strOrNull(enrollment["id"]), labelAr: str(enrollment["label_ar"]) }
      : null,
    platform: str(d["platform"]),
    platformLabelAr: str(d["platform_label_ar"]),
    externalRef: strOrNull(d["external_ref"]),
    status: str(d["status"]),
    statusLabelAr: str(d["status_label_ar"]),
    isNotificationDefault: d["is_notification_default"] === true,
    membersCount: num(d["members_count"]),
    checklist: list.map((c) => ({
      key: str(c["key"]),
      labelAr: str(c["label_ar"]),
      done: c["done"] === true,
    })),
    checklistDone: num(d["checklist_done"]),
    checklistTotal: num(d["checklist_total"]),
    checklistComplete: d["checklist_complete"] === true,
    version: num(d["version"], 1),
  };
}

function warningsOf(res: unknown): GroupWarning[] {
  const w = (res as unknown as { warnings?: unknown }).warnings;
  if (!Array.isArray(w)) return [];
  return (w as Record<string, unknown>[]).map((x) => ({
    code: str(x["code"]),
    messageAr: str(x["message_ar"]),
  }));
}

export async function fetchGroups(
  familyId: string,
): Promise<{ rows: GroupRow[]; warnings: GroupWarning[] }> {
  const path = "/groups";
  const res = await api.get<unknown>(path, { query: { family_id: familyId, per_page: 50 } });
  if (!Array.isArray(res.data)) fail(path, "الرد مش قائمة");
  return {
    rows: (res.data as Record<string, unknown>[]).map(toGroup),
    warnings: warningsOf(res),
  };
}

export async function fetchPendingGroupWork(): Promise<PendingGroupWork[]> {
  const path = "/groups/pending";
  const res = await api.get<unknown>(path);
  if (!Array.isArray(res.data)) fail(path, "الرد مش قائمة");
  return (res.data as Record<string, unknown>[]).map((d) => {
    const e = obj(d["enrollment"]);
    const f = obj(d["family"]);
    return {
      journeyId: str(d["journey_id"]),
      stepNo: num(d["step_no"]),
      enrollment: {
        id: str(e["id"]),
        labelAr: str(e["label_ar"]),
        subject: strOrNull(e["subject"]),
      },
      family: { id: str(f["id"]), nameAr: str(f["name_ar"]) },
    };
  });
}

export async function createGroup(input: {
  familyId: string;
  nameAr: string;
  type: string;
  enrollmentId?: string | null;
  studentId?: string | null;
  platform: string;
  externalRef?: string | null;
}): Promise<{ row: GroupRow; toastAr: string; warnings: GroupWarning[] }> {
  const path = "/groups";
  const res = await api.post<unknown>(path, {
    family_id: input.familyId,
    name_ar: input.nameAr,
    type: input.type,
    enrollment_id: input.enrollmentId ?? null,
    student_id: input.studentId ?? null,
    platform: input.platform,
    external_ref: input.externalRef ?? null,
  });
  const d = obj(res.data);
  if (!d["id"]) fail(path, "إنشاء الجروب رجّع رد فاضي");
  return { row: toGroup(d), toastAr: str(d["toast_ar"]), warnings: warningsOf(res) };
}

export async function setChecklistItem(
  groupId: string,
  key: string,
  done: boolean,
): Promise<{ row: GroupRow; toastAr: string }> {
  const path = `/groups/${groupId}/checklist/${key}`;
  const res = await api.patch<unknown>(path, { done });
  const d = obj(res.data);
  if (!d["id"]) fail(path, "تحديث البند رجّع رد فاضي");
  return { row: toGroup(d), toastAr: str(d["toast_ar"]) };
}

export async function setDefaultNotificationGroup(
  groupId: string,
): Promise<{ row: GroupRow; toastAr: string }> {
  const path = `/groups/${groupId}/default-notification`;
  const res = await api.post<unknown>(path);
  const d = obj(res.data);
  if (!d["id"]) fail(path, "تحديد جروب الإشعارات رجّع رد فاضي");
  return { row: toGroup(d), toastAr: str(d["toast_ar"]) };
}

export async function archiveGroup(
  groupId: string,
  reasonAr: string,
): Promise<{ row: GroupRow; toastAr: string }> {
  const path = `/groups/${groupId}/archive`;
  const res = await api.post<unknown>(path, { reason_ar: reasonAr });
  const d = obj(res.data);
  if (!d["id"]) fail(path, "الأرشفة رجّعت رد فاضي");
  return { row: toGroup(d), toastAr: str(d["toast_ar"]) };
}

/** أنواع الجروبات الخمسة — نفس `models/group.py::GroupType` */
export const GROUP_TYPES: { key: string; labelAr: string; hintAr: string }[] = [
  { key: "study", labelAr: "مذاكرة", hintAr: "لازم يتربط بمادة (طالب × مادة)" },
  { key: "evaluation", labelAr: "تقييم", hintAr: "لحصة التقييم" },
  { key: "reports", labelAr: "تقارير", hintAr: "للتقارير الشهرية" },
  { key: "teacher_hr", labelAr: "شؤون معلم", hintAr: "بين الأكاديمية والمعلم" },
  { key: "admin", labelAr: "إداري", hintAr: "داخلي" },
];

export const PLATFORMS: { key: string; labelAr: string }[] = [
  { key: "whatsapp", labelAr: "واتساب" },
  { key: "telegram", labelAr: "تليجرام" },
  { key: "google_chat", labelAr: "جوجل شات" },
];
